import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import type { DocumentApi, InspectionApi, ProjectApi, QualitySummaryApi, TreeApi } from "@/lib/api";
import { useCampaignsList } from "@/hooks";
import { NoRealData } from "@/components/shared";
import { riscoConfig, statusRevisaoConfig } from "@/config/cta-config";
import {
  DOCUMENT_TYPE_LABELS,
  NEUTRAL_BADGE,
  PROJECT_TYPE_LABELS,
  RISK_CLASS_ORDER,
  humanizeCode,
} from "@/config/projetos-config";
import { CAMPAIGN_STATUS_LABELS, CAMPAIGN_TYPE_LABELS } from "@/types/campaign";
import ProjectStatusBadge from "@/pages/projetos/components/ProjectStatusBadge";
import ProjectProgressBar from "@/pages/projetos/components/ProjectProgressBar";
import ProjectKpiCard from "@/pages/projetos/components/ProjectKpiCard";
import ProjectMapPreview from "./components/ProjectMapPreview";
import ProjectInspectionTable from "./components/ProjectInspectionTable";
import ProjectTeamCards from "./components/ProjectTeamCards";
import {
  ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from "recharts";

const tabs = [
  { id: "visao-geral", label: "Visão Geral", icon: "ri-dashboard-line" },
  { id: "arvores", label: "Exemplares (CTA)", icon: "ri-plant-line" },
  { id: "mapa", label: "Mapa", icon: "ri-map-2-line" },
  { id: "vistorias", label: "Vistorias", icon: "ri-clipboard-line" },
  { id: "equipe", label: "Campanhas e Equipe", icon: "ri-team-line" },
  { id: "documentos", label: "Documentos e Fontes", icon: "ri-file-list-3-line" },
];

interface LoadState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

/** Carrega uma lista vinculada ao projeto; enquanto nao ha project_id, fica vazia. */
function useProjectList<T>(projectId: number | null, fetcher: (id: number) => Promise<T[]>): LoadState<T[]> {
  const [state, setState] = useState<LoadState<T[]>>({ data: [], loading: projectId != null, error: null });
  useEffect(() => {
    if (projectId == null) return;
    let alive = true;
    setState({ data: [], loading: true, error: null });
    fetcher(projectId)
      .then((rows) => alive && setState({ data: rows, loading: false, error: null }))
      .catch((err) =>
        alive && setState({ data: [], loading: false, error: err instanceof Error ? err.message : "Erro ao carregar" })
      );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  return state;
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleDateString("pt-BR") : "—";
}

function monthKey(value: string) {
  return value.slice(0, 7);
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("visao-geral");
  const [projeto, setProjeto] = useState<ProjectApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoading(true);
    setError(null);
    // A lista linka por project_uuid; aceitamos tambem o id numerico.
    api
      .projects()
      .then((rows) => {
        if (!alive) return;
        const found = rows.find((p) => p.project_uuid === id || String(p.project_id) === id) ?? null;
        setProjeto(found);
      })
      .catch((err) => alive && setError(err instanceof Error ? err.message : "Erro ao carregar o projeto"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  const projectId = projeto?.project_id ?? null;
  const arvores = useProjectList<TreeApi>(projectId, (pid) => api.trees(pid));
  const vistorias = useProjectList<InspectionApi>(projectId, (pid) => api.inspections(pid));
  const documentos = useProjectList<DocumentApi>(projectId, (pid) => api.documents(pid));
  const qualidade = useProjectList<QualitySummaryApi>(projectId, () => api.qualitySummary());
  const { campaigns, loading: campanhasLoading } = useCampaignsList(projectId ?? undefined);
  const campanhas = projectId == null ? [] : campaigns.filter((c) => c.projectId === projectId);

  const qualidadeProjeto = qualidade.data.find((q) => q.project_id === projectId) ?? null;

  // Distribuicao pela ultima classe de risco registrada. Sem avaliacao fica separado:
  // nao e "baixo".
  const riscoData = useMemo(() => {
    const rows = RISK_CLASS_ORDER.map((r) => ({
      name: r.label,
      color: r.color,
      value: arvores.data.filter((a) => a.latest_risk_class === r.key).length,
    }));
    const semAvaliacao = arvores.data.filter(
      (a) => !a.latest_risk_class || !RISK_CLASS_ORDER.some((r) => r.key === a.latest_risk_class)
    ).length;
    return [...rows, { name: "Sem avaliação", color: "#9AA0A6", value: semAvaliacao }];
  }, [arvores.data]);

  // Evolucao mensal a partir das datas reais de cadastro e de vistoria.
  const evolucaoMensal = useMemo(() => {
    const acc: Record<string, { cadastros: number; vistorias: number }> = {};
    for (const a of arvores.data) {
      if (!a.created_at) continue;
      const k = monthKey(a.created_at);
      acc[k] = acc[k] ?? { cadastros: 0, vistorias: 0 };
      acc[k].cadastros += 1;
    }
    for (const v of vistorias.data) {
      if (!v.inspection_date) continue;
      const k = monthKey(v.inspection_date);
      acc[k] = acc[k] ?? { cadastros: 0, vistorias: 0 };
      acc[k].vistorias += 1;
    }
    return Object.keys(acc)
      .sort()
      .map((k) => {
        const [ano, mes] = k.split("-");
        return { mes: `${mes}/${ano.slice(2)}`, ...acc[k] };
      });
  }, [arvores.data, vistorias.data]);

  if (loading) {
    return (
      <div className="bg-white rounded-card border border-background-200 p-12 text-center">
        <p className="text-sm text-foreground-500">Carregando projeto...</p>
      </div>
    );
  }

  if (!projeto) {
    return (
      <div className="bg-white rounded-card border border-background-200 p-12 text-center">
        <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-folder-chart-line text-foreground-400 text-2xl"></i>
        </div>
        <h3 className="text-lg font-semibold text-foreground-900 mb-2">Projeto não encontrado</h3>
        <p className="text-sm text-foreground-500 mb-4">
          {error ?? "O projeto solicitado não existe ou foi removido."}
        </p>
        <button onClick={() => navigate("/projetos")} className="btn-primary cursor-pointer">
          <i className="ri-arrow-left-line"></i>
          Voltar para Projetos
        </button>
      </div>
    );
  }

  const tipoLabel = PROJECT_TYPE_LABELS[projeto.project_type] ?? humanizeCode(projeto.project_type);
  const novoExemplarUrl = `/cta/novo?project_id=${projeto.project_id}`;

  return (
    <div className="space-y-4">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="hover:text-foreground-700 transition-colors">Dashboard</Link>
        <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
        <Link to="/projetos" className="hover:text-foreground-700 transition-colors">Projetos</Link>
        <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
        <span className="text-foreground-400">Detalhe do Projeto</span>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-card border border-background-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-h4 font-bold text-foreground-900 font-heading">{projeto.name}</h1>
              <ProjectStatusBadge status={projeto.status.toLowerCase()} />
            </div>
            <p className="text-sm text-foreground-500">
              {tipoLabel}
              {projeto.municipality_name && ` · ${projeto.municipality_name}${projeto.uf ? `/${projeto.uf}` : ""}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <button onClick={() => navigate(novoExemplarUrl)} className="btn-primary text-xs px-3 py-2 cursor-pointer">
              <i className="ri-add-line"></i>
              Novo exemplar
            </button>
            <button onClick={() => navigate("/mapa")} className="btn-outline text-xs px-3 py-2 cursor-pointer">
              <i className="ri-map-2-line"></i>
              Abrir mapa
            </button>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-card border border-background-200 p-4">
          <p className="text-xs text-foreground-500 font-medium mb-1">Cliente / Contratante</p>
          <div className="flex items-center gap-1.5">
            <i className="ri-building-line text-foreground-400 text-sm"></i>
            <p className="text-sm font-medium text-foreground-900">{projeto.client_name ?? "—"}</p>
          </div>
        </div>
        <div className="bg-white rounded-card border border-background-200 p-4">
          <p className="text-xs text-foreground-500 font-medium mb-1">Criado em</p>
          <div className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-foreground-400 text-sm"></i>
            <p className="text-sm text-foreground-900">{formatDate(projeto.created_at)}</p>
          </div>
        </div>
        <div className="bg-white rounded-card border border-background-200 p-4">
          <p className="text-xs text-foreground-500 font-medium mb-1">Última atualização</p>
          <div className="flex items-center gap-1.5">
            <i className="ri-time-line text-foreground-400 text-sm"></i>
            <p className="text-sm text-foreground-900">{formatDate(projeto.updated_at)}</p>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-card border border-background-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-foreground-900">Progresso do Projeto</p>
          <span className="text-sm font-bold text-foreground-900 font-heading tabular-nums">{projeto.progress_percent}%</span>
        </div>
        <ProjectProgressBar progresso={projeto.progress_percent} size="md" showLabel={false} />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-card border border-background-200 overflow-hidden">
        <div className="flex items-center gap-0 border-b border-background-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === tab.id
                  ? "text-primary-700 border-b-2 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* Visão Geral */}
          {activeTab === "visao-geral" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <ProjectKpiCard label="Exemplares cadastrados" value={projeto.total_trees.toLocaleString("pt-BR")} icon="ri-plant-line" iconColor="text-primary-600" iconBg="bg-primary-50" sublabel={`${projeto.active_trees.toLocaleString("pt-BR")} ativos`} />
                <ProjectKpiCard label="Vistorias" value={projeto.inspection_count.toLocaleString("pt-BR")} icon="ri-check-double-line" iconColor="text-accent-600" iconBg="bg-accent-50" sublabel={`Última: ${formatDate(projeto.latest_inspection_date)}`} />
                <ProjectKpiCard label="Risco alto/crítico" value={projeto.high_risk_trees.toLocaleString("pt-BR")} icon="ri-alert-line" iconColor="text-danger-600" iconBg="bg-danger-50" sublabel="Pela última avaliação de risco" />
                <ProjectKpiCard label="Espécies identificadas" value={projeto.species_count.toLocaleString("pt-BR")} icon="ri-leaf-line" iconColor="text-accent-600" iconBg="bg-accent-50" />
                <ProjectKpiCard label="Campanhas" value={campanhasLoading ? "—" : campanhas.length} icon="ri-flag-line" iconColor="text-secondary-600" iconBg="bg-secondary-50" />
                <ProjectKpiCard label="Documentos" value={documentos.loading ? "—" : documentos.data.length} icon="ri-file-list-3-line" iconColor="text-secondary-600" iconBg="bg-secondary-50" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Evolução mensal de cadastros e vistorias</h3>
                  {evolucaoMensal.length === 0 ? (
                    <NoRealData icon="ri-bar-chart-line" title="Sem cadastros ou vistorias" description="O gráfico aparece quando houver exemplares ou vistorias no projeto." />
                  ) : (
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={evolucaoMensal}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" />
                          <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#6B737A" }} axisLine={false} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#6B737A" }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }} />
                          <Bar dataKey="cadastros" fill="#1B6E4F" radius={[3, 3, 0, 0]} barSize={10} name="Cadastros" />
                          <Bar dataKey="vistorias" fill="#0052CC" radius={[3, 3, 0, 0]} barSize={10} name="Vistorias" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Classificação de risco (última avaliação)</h3>
                  {arvores.data.length === 0 ? (
                    <NoRealData icon="ri-alert-line" title="Sem exemplares" description="A distribuição de risco aparece quando houver exemplares no projeto." />
                  ) : (
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={riscoData} layout="vertical" margin={{ left: 0, right: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E8EAED" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#6B737A" }} axisLine={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#454B50" }} width={90} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E8EAED", fontSize: "12px" }} />
                          <Bar dataKey="value" radius={[0, 3, 3, 0]} barSize={14} name="Exemplares">
                            {riscoData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Qualidade do cadastro</h3>
                  {qualidadeProjeto ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 p-4 rounded-lg bg-primary-50 border border-primary-200">
                        <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shrink-0">
                          <span className="text-lg font-bold text-primary-600 font-heading">
                            {Math.round(qualidadeProjeto.estimated_quality_score)}%
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground-900 font-heading">Índice estimado de qualidade</p>
                          <p className="text-xs text-foreground-500">Calculado pelas regras de validação do banco</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        {[
                          { label: "Em aberto", value: qualidadeProjeto.open_issue_count },
                          { label: "Críticas", value: qualidadeProjeto.critical_issue_count },
                          { label: "Altas", value: qualidadeProjeto.high_issue_count },
                          { label: "Médias", value: qualidadeProjeto.medium_issue_count },
                        ].map((q) => (
                          <div key={q.label} className="bg-background-50 rounded-lg p-2">
                            <p className="text-sm font-bold text-foreground-900 tabular-nums">{q.value}</p>
                            <p className="text-[10px] text-foreground-500">{q.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <NoRealData
                      icon="ri-shield-check-line"
                      title={qualidade.loading ? "Carregando..." : "Sem resumo de qualidade"}
                      description="O resumo vem das regras de validação do banco e ainda não há resultado para este projeto."
                    />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Últimas atividades</h3>
                  <NoRealData
                    icon="ri-history-line"
                    title="Linha do tempo do projeto"
                    description="Ainda não há fonte real de atividades por projeto. O histórico de cada exemplar está na ficha dele."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Exemplares */}
          {activeTab === "arvores" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-foreground-900 font-heading">
                  Exemplares vinculados ao projeto
                  {!arvores.loading && (
                    <span className="ml-2 text-xs font-normal text-foreground-500">({arvores.data.length})</span>
                  )}
                </h3>
                <button onClick={() => navigate(novoExemplarUrl)} className="btn-primary text-xs px-3 py-2 cursor-pointer">
                  <i className="ri-add-line"></i>
                  Novo exemplar
                </button>
              </div>

              {arvores.loading ? (
                <p className="text-sm text-foreground-500 text-center py-8">Carregando exemplares...</p>
              ) : arvores.error ? (
                <p className="text-sm text-danger-600 text-center py-8">Não foi possível carregar: {arvores.error}</p>
              ) : arvores.data.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-plant-line text-primary-500 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">Nenhum exemplar cadastrado</h3>
                  <p className="text-sm text-foreground-500 mb-4">Este projeto ainda não possui exemplares arbóreos.</p>
                  <button onClick={() => navigate(novoExemplarUrl)} className="btn-primary cursor-pointer">
                    <i className="ri-add-line"></i>
                    Cadastrar primeiro exemplar
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto border border-background-200 rounded-lg">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-50">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Código CTA</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Espécie</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Bairro/Logradouro</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Risco</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Última vistoria</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Revisão</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arvores.data.map((arv) => {
                        const rsk = arv.latest_risk_class ? riscoConfig[arv.latest_risk_class] : undefined;
                        const st = arv.review_status ? statusRevisaoConfig[arv.review_status] : undefined;
                        const local = [arv.neighborhood, arv.street].filter(Boolean).join(" / ");
                        return (
                          <tr key={arv.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                            <td className="px-4 py-3">
                              <span className="text-xs font-mono text-foreground-800 whitespace-nowrap">{arv.cta_code ?? "—"}</span>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                                {arv.species_popular_name ?? "Não identificada"}
                              </p>
                              {arv.species_scientific_name && (
                                <p className="text-xs text-foreground-400 italic">{arv.species_scientific_name}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs text-foreground-600">{local || "—"}</span>
                            </td>
                            <td className="px-4 py-3">
                              {arv.latest_risk_class ? (
                                <span className={`badge ${rsk?.badge ?? NEUTRAL_BADGE}`}>{rsk?.label ?? humanizeCode(arv.latest_risk_class)}</span>
                              ) : (
                                <span className="text-xs text-foreground-400">Sem avaliação</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-foreground-700 whitespace-nowrap">{formatDate(arv.latest_inspection_date)}</span>
                            </td>
                            <td className="px-4 py-3">
                              {arv.review_status ? (
                                <span className={`badge ${st?.badge ?? NEUTRAL_BADGE}`}>{st?.label ?? humanizeCode(arv.review_status)}</span>
                              ) : (
                                <span className="text-xs text-foreground-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => navigate(`/cta/${arv.id}`)}
                                className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer"
                                title="Ver ficha do exemplar"
                              >
                                <i className="ri-eye-line text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Mapa */}
          {activeTab === "mapa" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground-900 font-heading">Mapa do projeto</h3>
                <button onClick={() => navigate("/mapa")} className="btn-primary text-xs px-3 py-2 cursor-pointer">
                  <i className="ri-map-2-line"></i>
                  Abrir mapa completo
                </button>
              </div>
              {arvores.loading ? (
                <p className="text-sm text-foreground-500 text-center py-8">Carregando exemplares...</p>
              ) : (
                <ProjectMapPreview arvores={arvores.data} />
              )}
            </div>
          )}

          {/* Vistorias */}
          {activeTab === "vistorias" && (
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Vistorias do projeto</h3>
              {vistorias.loading ? (
                <p className="text-sm text-foreground-500 text-center py-8">Carregando vistorias...</p>
              ) : vistorias.error ? (
                <p className="text-sm text-danger-600 text-center py-8">Não foi possível carregar: {vistorias.error}</p>
              ) : vistorias.data.length === 0 ? (
                <NoRealData
                  icon="ri-clipboard-line"
                  title="Nenhuma vistoria registrada"
                  description="As vistorias nascem pela ficha de cada exemplar arbóreo do projeto."
                />
              ) : (
                <ProjectInspectionTable vistorias={vistorias.data} />
              )}
            </div>
          )}

          {/* Campanhas e Equipe */}
          {activeTab === "equipe" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Campanhas de campo</h3>
                {campanhasLoading ? (
                  <p className="text-sm text-foreground-500">Carregando campanhas...</p>
                ) : campanhas.length === 0 ? null : (
                  <div className="overflow-x-auto border border-background-200 rounded-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-50">
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Campanha</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Tipo</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Status</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Período</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Exemplares</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Quadras</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campanhas.map((c) => (
                          <tr key={c.id} className="border-b border-background-200">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-foreground-900">{c.name}</p>
                              <p className="text-xs font-mono text-foreground-500">{c.code}</p>
                            </td>
                            <td className="px-4 py-3 text-xs text-foreground-600">{CAMPAIGN_TYPE_LABELS[c.campaignType] ?? humanizeCode(c.campaignType)}</td>
                            <td className="px-4 py-3 text-xs text-foreground-600">{CAMPAIGN_STATUS_LABELS[c.status] ?? humanizeCode(c.status)}</td>
                            <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">
                              {formatDate(c.startDate)} → {formatDate(c.endDate)}
                            </td>
                            <td className="px-4 py-3 text-sm tabular-nums text-foreground-700">
                              {c.registeredTreeCount}
                              {c.targetTreeCount != null && <span className="text-foreground-400"> / {c.targetTreeCount}</span>}
                            </td>
                            <td className="px-4 py-3 text-sm tabular-nums text-foreground-700">
                              {c.quadrasConcluidas} / {c.quadraCount}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">Equipe técnica</h3>
                {!campanhasLoading && <ProjectTeamCards campanhas={campanhas} />}
              </div>
            </div>
          )}

          {/* Documentos e Fontes */}
          {activeTab === "documentos" && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground-900 font-heading">Documentos e fontes</h3>
              {documentos.loading ? (
                <p className="text-sm text-foreground-500 text-center py-8">Carregando documentos...</p>
              ) : documentos.error ? (
                <p className="text-sm text-danger-600 text-center py-8">Não foi possível carregar: {documentos.error}</p>
              ) : documentos.data.length === 0 ? (
                <NoRealData icon="ri-file-list-3-line" title="Nenhum documento vinculado" description="Documentos e fontes vinculados a este projeto aparecem aqui." />
              ) : (
                <div className="overflow-x-auto border border-background-200 rounded-lg">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-50">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Documento</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Tipo</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Número</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Emissão</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentos.data.map((doc) => (
                        <tr key={doc.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <i className="ri-file-line text-foreground-400"></i>
                              <span className="text-sm font-medium text-foreground-900">{doc.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md uppercase whitespace-nowrap">
                              {DOCUMENT_TYPE_LABELS[doc.document_type] ?? humanizeCode(doc.document_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-foreground-600 whitespace-nowrap">{doc.document_number ?? "—"}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(doc.issue_date)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`badge ${NEUTRAL_BADGE}`}>{humanizeCode(doc.status)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
